/**
 * Long-form articles for /insights. Each article is plain data so the
 * index and the article page render from one place.
 *
 * Writing rules (same as content.ts, plus):
 *   - Every incident detail must trace to a source in the article's list.
 *   - Say plainly what code scanning would NOT have stopped.
 *   - Product claims stay inside what the scanner actually runs (see the
 *     claim rules in content.ts). No scheduled rescans, no workflow
 *     analysis, no zero-day malware detection.
 *   - No em or en dashes in visible copy.
 */

import type { Source } from "./content";

export type ArticleBlock =
  | { kind: "p"; text: string }
  | { kind: "h2"; text: string }
  | { kind: "list"; items: string[]; ordered?: boolean }
  | { kind: "quote"; text: string }
  | { kind: "callout"; title: string; items: string[] }
  | { kind: "figure"; name: string; caption?: string }
  | { kind: "table"; head: string[]; rows: string[][]; note?: string }
  | { kind: "code"; text: string }
  | { kind: "facts"; items: Array<{ k: string; v: string }> };

export type Article = {
  slug: string;
  title: string;
  dek: string;
  author: string;
  role?: string;
  date: string;
  readMinutes: number;
  blocks: ArticleBlock[];
  sources: Source[];
  /** Closing panel copy; the page has a default. */
  cta?: { title: string; em: string; body: string };
};

const KERNEL_TCP_UAF: Article = {
  slug: "cve-2026-98432-linux-tcp-use-after-free",
  title: "CVE-2026-98432 explained: a 9.8 bug hiding in the Linux TCP handshake",
  dek:
    "A race in how Linux handles new IPv4 connections can let an unauthenticated attacker corrupt kernel memory. Here is how it works, in pictures, and exactly what to do about it this week.",
  author: "Yash Sancheti",
  date: "1 November 2026",
  readMinutes: 8,
  cta: {
    title: "Know what you run.",
    em: "Before the next one.",
    body: "Paxley scans your code, dependencies and secrets in one pass, so the next advisory is a lookup, not a hunt.",
  },
  blocks: [
    {
      kind: "p",
      text:
        "Most kernel bugs need a foothold first: a local account, a malicious app, a container someone already broke into. CVE-2026-98432 does not. It sits in the code that answers brand new TCP connections, which is to say code that every Linux server on the internet runs all day long. It was published on 15 October with a CVSS score of 9.8, and on 27 October Anthropic described a working remote exploit that ran commands as root on Linux servers with a Multipath TCP listener.",
    },
    {
      kind: "p",
      text:
        "If you run Linux anywhere, this one deserves ten minutes of your attention. Here is what is going on, without the kernel-developer jargon.",
    },
    {
      kind: "facts",
      items: [
        { k: "Severity", v: "CVSS 3.1 score 9.8, Critical: reachable over the network, no privileges, no user interaction. Red Hat also rates it Critical." },
        { k: "Affected", v: "Linux 4.4 and later, IPv4 TCP. The main bug does not affect IPv6." },
        { k: "Found by", v: "Anthropic's Claude, reported through coordinated disclosure. Fixed upstream by Eric Dumazet." },
        { k: "Exploit", v: "Root code execution demonstrated on servers with an MPTCP listener. No public exploit code and no confirmed attacks in the wild at the time of writing." },
        { k: "Fix", v: "Upstream commit e3f33b0a1d89, backported to every maintained stable branch." },
      ],
    },

    { kind: "h2", text: "First, how a TCP connection starts" },
    {
      kind: "p",
      text:
        "Every TCP connection begins with a three-step handshake. The client sends a SYN, the server answers with a SYN-ACK, and the client confirms with an ACK. The SYN can carry IPv4 options, a rarely used field in the IP header. When it does, the Linux kernel saves a copy of those options so it can use them when it builds its SYN-ACK reply. Once the handshake finishes, the kernel creates the real connection socket and hands the saved options over to it.",
    },
    {
      kind: "figure",
      name: "handshake",
      caption: "The normal flow. The important detail is step 3: the new socket takes over the same block of memory that the SYN-ACK code reads from.",
    },
    {
      kind: "p",
      text:
        "Notice the word \"over\". The options are not copied to the new socket. Ownership of the same piece of memory moves from the pending request to the finished connection. That shortcut is fine, as long as nobody else is still looking at that memory.",
    },

    { kind: "h2", text: "Where it goes wrong" },
    {
      kind: "p",
      text:
        "Servers handle connections on many CPU cores at once, and networks are messy. A client can send its SYN twice, for example when the first one seems to have been lost. So the kernel can be finishing the handshake on one core while another core is answering a retransmitted SYN for the very same connection request.",
    },
    {
      kind: "figure",
      name: "race",
      caption: "The race. CPU 1 frees the options the moment the connection closes. CPU 2 was still about to read them.",
    },
    {
      kind: "p",
      text:
        "The code that builds the SYN-ACK reads the options inside what the kernel calls an RCU read section. RCU (read, copy, update) works like a promise: readers may look at shared data without heavy locking, and in exchange anyone who frees that data must wait until every reader that might still be looking has finished. The connection socket broke that promise. When it was destroyed, it freed the options straight away with a plain kfree() instead of waiting.",
    },
    {
      kind: "p",
      text:
        "So if the timing lines up, CPU 2 reads memory that has already been handed back to the kernel and may already hold something else. That is a use-after-free, and in kernel networking code it is about as serious as memory bugs get. Depending on what now sits in that memory, the result can be a crash, a leak of kernel data or, with enough control over what gets placed there, code running with full kernel privileges.",
    },
    {
      kind: "p",
      text:
        "There is a second road to the same mistake. When a listening socket closes and the kernel moves its pending connection requests to another listener (a feature switched on with net.ipv4.tcp_migrate_req), the cloned request shares the same options pointer. Same memory, two owners, same problem.",
    },

    { kind: "h2", text: "Why 9.8, and how hard is it really?" },
    {
      kind: "p",
      text:
        "The score reflects what an attacker needs: network access to a listening port, no password, and nobody on your side clicking anything. Taken at face value, that is the worst possible combination.",
    },
    {
      kind: "p",
      text:
        "Two things soften it in practice, and one makes it worse. Races are hard to win reliably, and Anthropic says its proof of concept needed a long, retryable race before it succeeded. There is also no public exploit code yet. But the demonstrated attack used Multipath TCP (MPTCP) listeners, and since Go 1.24, Go programs on Linux turn on MPTCP for listening sockets by default. The Go security team has also said the underlying bug is not specific to MPTCP, so switching MPTCP off narrows the known path without closing the bug.",
    },
    {
      kind: "quote",
      text: "Hard to exploit is not the same as safe. A working exploit already exists in at least one lab, and the bug is now public.",
    },

    { kind: "h2", text: "The fix is one idea" },
    {
      kind: "p",
      text:
        "The patch changes how the options are freed. Instead of kfree(), the kernel now uses kfree_rcu(), which waits for an RCU grace period: the moment when every reader that could have been holding the old pointer is guaranteed to be done. The memory still gets freed, just a little later, once it is safe.",
    },
    {
      kind: "figure",
      name: "fix",
      caption: "After the fix. Freeing waits for the grace period, so CPU 2 always finishes with valid memory.",
    },

    { kind: "h2", text: "Are you affected?" },
    {
      kind: "p",
      text:
        "If a Linux machine accepts IPv4 TCP connections and is not running a fixed kernel, treat it as affected. That includes container hosts, because containers share the host kernel, and appliances that run Linux under the hood. Rebuilding your container images does nothing for this one. The fix has to land on the host.",
    },
    { kind: "figure", name: "decide" },
    { kind: "p", text: "Three quick checks on any host:" },
    {
      kind: "code",
      text:
        "uname -r                          # running kernel version\nsysctl net.mptcp.enabled          # 1 = new MPTCP sockets allowed\nsysctl net.ipv4.tcp_migrate_req   # 1 = second vulnerable path enabled",
    },

    { kind: "h2", text: "Fixed versions" },
    {
      kind: "table",
      head: ["Kernel line", "Fixed in"],
      rows: [
        ["5.10", "5.10.272"],
        ["5.15", "5.15.223"],
        ["6.1", "6.1.190"],
        ["6.6", "6.6.159"],
        ["6.12", "6.12.113"],
        ["6.18", "6.18.56"],
        ["7.2", "7.2.10"],
        ["Mainline", "7.3"],
      ],
      note:
        "Upstream stable releases. Distribution kernels use their own version numbers, so check your vendor. For example, Debian 12 is fixed in 6.1.190-1 (DLA-4841-1) and Debian 13 in 6.12.113-1 (DSA-6568-1). AlmaLinux published fixed kernels for 8, 9 and 10 in its testing repository first. Red Hat lists its status on its CVE page.",
    },

    { kind: "h2", text: "If you can't reboot today" },
    { kind: "p", text: "None of these fix the bug. They shrink the window until you can install the fixed kernel." },
    {
      kind: "list",
      items: [
        "Drop incoming IPv4 TCP packets that carry IP options, at the host firewall or the network edge. Legitimate traffic almost never uses them. Check first if you rely on CIPSO security labels, and remember a host firewall may not see traffic between containers on the same machine.",
        "Turn off MPTCP if you don't need it (net.mptcp.enabled=0, made persistent), then restart services that already have MPTCP listeners open. Go services can also set GODEBUG=multipathtcp=0, but the Go team calls per-program switches fragile: one forgotten listener and you are exposed again.",
        "Set net.ipv4.tcp_migrate_req=0 unless you depend on request migration. It removes one of the two vulnerable paths.",
        "Consider booting with init_on_free=1, which zeroes memory when it is freed, so a stale read returns zeros instead of leftover data. Expect a small performance cost, often quoted at 1 to 5 percent.",
      ],
    },

    { kind: "h2", text: "The bigger story" },
    {
      kind: "p",
      text:
        "The most interesting thing about this CVE may be who found it. A use-after-free that sat in one of the most heavily reviewed code paths in open source since Linux 4.4 was found by an AI model, which then helped turn it into a working remote exploit. Anthropic's own advice in its write-up is that defenders should use the same capability first: scan your code, and check pull requests before bugs ship.",
    },
    {
      kind: "p",
      text:
        "We agree, and it cuts both ways. The gap between a bug being findable and a bug being exploited is shrinking. The teams that cope are the ones that already know what they run: which kernels sit under which services, which services are built with a toolchain that turns MPTCP on by default, which images and dependencies need rebuilding. That inventory is boring right up until the week it is the only thing that matters.",
    },
    {
      kind: "callout",
      title: "Your checklist for this week",
      items: [
        "List every Linux host that accepts IPv4 TCP, starting with anything internet-facing.",
        "Install the fixed kernel from your distribution and reboot. Confirm with uname -r.",
        "Until then: drop IPv4 packets with IP options, disable MPTCP where it isn't needed, and set tcp_migrate_req=0.",
        "List your Go services built with Go 1.24 or later and decide whether they need MPTCP at all.",
        "Watch for a public exploit or a CISA KEV listing. If either appears, anything still unpatched becomes an incident.",
      ],
    },
  ],
  sources: [
    { label: "CVE record: CVE-2026-98432", url: "https://www.cve.org/CVERecord?id=CVE-2026-98432" },
    { label: "Upstream fix: commit e3f33b0a1d89", url: "https://git.kernel.org/stable/c/e3f33b0a1d89d738e2b913dab519226eaf76e15b" },
    { label: "Anthropic: Discovering vulnerabilities with Claude", url: "https://www.anthropic.com/research/discovering-vulnerabilities-with-claude" },
    { label: "Red Hat: CVE-2026-98432", url: "https://access.redhat.com/security/cve/cve-2026-98432" },
    { label: "Debian security tracker: CVE-2026-98432", url: "https://security-tracker.debian.org/tracker/CVE-2026-98432" },
    { label: "AlmaLinux: CVE-2026-98432 mitigations and fixed kernels", url: "https://almalinux.org/blog/2026-10-30-cve-2026-98432/" },
    { label: "oss-security: Go Security Team on MPTCP and CVE-2026-98432", url: "https://www.openwall.com/lists/oss-security/2026/10/30/1" },
    { label: "oss-security: disabling MPTCP system-wide", url: "https://www.openwall.com/lists/oss-security/2026/10/30/6" },
    { label: "oss-security: init_on_free=1 as an interim mitigation", url: "https://www.openwall.com/lists/oss-security/2026/10/31/5" },
  ],
};

export const ARTICLES: Article[] = [
  KERNEL_TCP_UAF,
  {
    slug: "black-october-2026",
    title: "Black October: what code security could have stopped, and what it couldn't",
    dek:
      "Nineteen incidents in eighteen days. We sorted them by the question that matters to an engineering team: would anything in our own development process have changed the outcome?",
    author: "Yash Sancheti",
    date: "29 October 2026",
    readMinutes: 9,
    blocks: [
      {
        kind: "p",
        text:
          "If you work anywhere near software, you have probably seen the list by now. Backdoored npm packages, a poisoned PyPI release, a WordPress plugin that shipped malware through its own update channel, a cloud CLI with swapped binaries, critical holes in PHP, a certificate authority whose signing keys turned out to be guessable, and a ransomware attack that took 495 organisations offline in one go. People have started calling it Black October, and it is hard to argue with the name.",
      },
      {
        kind: "p",
        text:
          "We did not want to write another recap. Instead we read the advisories and incident reports for each item and asked a narrower question: if you were the engineering team on the receiving end, would anything in your development process have changed what happened to you? Sometimes the honest answer is yes. Sometimes it is no, and we will say so.",
      },
      {
        kind: "p",
        text: "The incidents fall into four groups.",
      },

      { kind: "h2", text: "1. The release was poisoned, not the code" },
      {
        kind: "p",
        text:
          "The biggest group this month has a pattern that should worry every team that installs open-source software, which is every team. The attacker did not find a bug in the project. They got hold of the way the project ships.",
      },
      {
        kind: "list",
        items: [
          "5 October: @subql/common 5.8.3 was published through a hijacked release workflow with a credential stealer and remote shell attached. It ran through a postinstall hook, and researchers noted that turning off install scripts alone would not have removed the risk.",
          "8 October: tensorlake 0.5.144 on npm carried a Shai-Hulud style worm. The malicious code went into the real repository first, so the package even shipped with valid provenance. Revoking the stolen GitHub token reportedly triggered a wipe of the victim's home directory.",
          "15 to 16 October: Rank Math PRO 3.0.123 delivered a backdoor to WordPress sites that updated during an eleven hour window.",
          "19 October: OVHcloud CLI release binaries for v0.11.0 to v0.15.0 were replaced with builds carrying a secret stealer, after a developer's GitHub account was compromised. They were live for about 94 minutes.",
          "21 October: LiteLLM 1.107.0 went out to PyPI and the official Docker images with a credential stealer, pushed through LiteLLM's own release pipeline. The source tag on GitHub was clean. The thing people installed was not.",
        ],
      },
      {
        kind: "p",
        text:
          "Here is the uncomfortable part. No amount of scanning your own source code would have stopped these releases from being published. The code you wrote was fine. What you installed was not. So the useful question is not how to prevent the attack, it is how fast you find out you are exposed and how much damage is possible before you do.",
      },
      {
        kind: "p",
        text:
          "That is where the development process earns its keep. One detail from the OVHcloud incident says it all: the problem surfaced because a customer had pinned the expected hash of the binary, the check failed, and they opened an issue. Teams that pin versions and verify what they download are the ones who notice. Teams that pull latest are the ones who become the incident.",
      },
      {
        kind: "p",
        text:
          "The second thing that matters is being able to answer one question within minutes of an advisory landing: do we run this, and where? If the answer involves grepping forty repositories by hand, you are already behind. A software bill of materials (SBOM) for every repository turns that into a lookup. Known malicious package feeds then close the loop. Once a version is reported, as all five of these quickly were, a dependency scan should flag it as an active compromise that you remove, not a vulnerability you schedule for next sprint.",
      },
      {
        kind: "p",
        text:
          "To be clear about the limits: a scanner cannot recognise a malicious package nobody has reported yet. What it can do is shrink the time between the report and your response from days to the length of a scan.",
      },

      { kind: "h2", text: "2. Secrets were the real target" },
      {
        kind: "p",
        text:
          "Look at what the malware in group one was actually after. Credentials. npm tokens, GitHub tokens, cloud keys, AI provider keys. That theme runs through the rest of the month too.",
      },
      {
        kind: "list",
        items: [
          "GhostAction came back on 8 October, using compromised maintainer accounts to push a malicious workflow file into hundreds of repositories. This wave did not just read the CI secrets available at run time. It mined credentials from the entire git history.",
          "One account of the LiteLLM root cause traces it back to a key exposed in a public repository months earlier. Reports differ on the entry point, but a long-lived, over-privileged token sits in the middle of every version of the story.",
          "OpenCode disclosed on 17 October that AI provider keys saved in its hosted Console may have been exposed. Railway disclosed on 21 October that customer environment variables were exposed for a subset of customers and asked everyone to rotate.",
        ],
      },
      {
        kind: "p",
        text:
          "Two lessons come out of this, and only one of them is about scanning.",
      },
      {
        kind: "p",
        text:
          "The first is that a secret committed to git never really goes away. Deleting it in a later commit removes it from the latest version of the file, not from history, and GhostAction showed that attackers now go looking in history on purpose. Secret scanning belongs in the development process for exactly this reason: on every pull request so new keys never land, and across the full history at least once so you know what is already sitting there.",
      },
      {
        kind: "p",
        text:
          "The second is about blast radius. When a platform you rely on is breached, as with OpenCode and Railway, nothing in your repository could have prevented it. What decides whether that is a bad afternoon or a bad month is whether you know which keys you have, where they are stored and how quickly you can rotate them. Short-lived credentials, OIDC for deployments instead of static cloud keys, and least privilege on CI tokens all turn a stolen secret into a much smaller problem.",
      },

      { kind: "h2", text: "3. Flaws in software everyone depends on" },
      {
        kind: "p",
        text:
          "Then there were the classic vulnerabilities. On 21 October PHP disclosed two critical unauthenticated remote code execution flaws, CVE-2026-109618 and CVE-2026-109619, with fixes in 8.5.12, 8.4.27, 8.3.36 and 8.2.35. The same week brought security releases for Node.js and BIND 9 and a batch of Linux kernel issues.",
      },
      {
        kind: "p",
        text:
          "You cannot prevent a bug in PHP. You can control two things: whether you know which of your applications and container images run an affected version, and whether you patch the ones that are actually being exploited first. That means dependency and runtime tracking that covers base images and language runtimes, not just application libraries, plus prioritisation driven by real exploitation data such as the CISA Known Exploited Vulnerabilities catalogue and EPSS rather than raw CVSS scores. A Dockerfile that says FROM php:8.3 with no digest is a small thing to flag in review and a big thing to discover during an emergency.",
      },
      {
        kind: "p",
        text:
          "The eMudhra incident deserves its own sentence. Eight intermediate certificate authority keys were weak enough to be recovered from public certificates; one private key was reportedly just the number 4. Browsers blocked the certificates and the company is leaving the public TLS business. It is an extreme case, but it is a reminder that cryptography is code too. Key generation, key sizes and hard-coded keys deserve the same review and automated checks as any other security-sensitive path.",
      },
      {
        kind: "p",
        text:
          "And it is worth saying the obvious thing. Your own code is still where most of your exposure lives. Exploited vulnerabilities are now the most common way into an organisation, ahead of phishing. Static analysis on every pull request is not glamorous, but it is the part of the process you fully control.",
      },

      { kind: "h2", text: "4. What a code scanner would not have stopped" },
      {
        kind: "p",
        text:
          "We would lose your trust if we claimed everything on the list was a code problem. It was not.",
      },
      {
        kind: "p",
        text:
          "The ransomware attack on IDCF Cloud on 7 October, which hit 495 companies and local governments in Japan, is an infrastructure and operations story. Capcom's disclosure on 22 October of unauthorised access to 11.77 million CAPCOM ID records is, by early accounts, about network access rather than application code. Several other items on the list are the same. These are won or lost on identity, patching of edge devices, segmentation, monitoring and backups you have actually tested restoring. A code scanner will not help you there, and anyone who says otherwise is selling something.",
      },

      { kind: "h2", text: "What we would change in the development process" },
      {
        kind: "p",
        text:
          "Pulling it together, here is what we think a sensible team does after a month like this. None of it is exotic. Most of it is unglamorous discipline that pays off precisely in weeks like the one we just had.",
      },
      {
        kind: "list",
        ordered: true,
        items: [
          "Lock and pin. Commit lockfiles, pin versions and image digests, and verify checksums for anything downloaded in CI.",
          "Keep an SBOM for every repository, so the question \"do we run this?\" takes minutes, not days.",
          "Scan dependencies against known malicious package feeds as well as CVEs, and treat a malicious hit as an incident: remove, rotate, rebuild.",
          "Scan for secrets on every pull request and across the full git history, then move deployments to short-lived credentials.",
          "Run static analysis on every pull request, reporting only what that change introduces, so developers actually read it.",
          "Prioritise fixes by real-world exploitation (KEV, EPSS, reachability), not by the count of findings.",
          "Rescan the moment a major advisory lands. A scan from last month answers last month's question.",
        ],
      },
      {
        kind: "callout",
        title: "If you only have an hour this week",
        items: [
          "Search lockfiles, caches and build logs for @subql/common 5.8.3, tensorlake 0.5.144 and litellm 1.107.0. Any hit means treat the machine as compromised.",
          "Check WordPress sites for Rank Math PRO 3.0.123 and OVHcloud CLI installs downloaded on 19 October.",
          "Look in .github/workflows for unexpected files such as security-audit.yml or github_actions_security.yml.",
          "Rotate any AI provider keys stored in OpenCode Console and any secrets held in Railway environment variables.",
          "Upgrade PHP to 8.5.12, 8.4.27, 8.3.36 or 8.2.35, and rebuild images that pin older tags.",
        ],
      },

      { kind: "h2", text: "Where Paxley fits" },
      {
        kind: "p",
        text:
          "We built Paxley because most teams cannot run and reconcile six separate tools to get this coverage. A single Paxley scan runs static analysis, dependency analysis that flags packages reported as malicious at the top of the queue with the advice to remove rather than upgrade, secret detection across git history, infrastructure as code checks, licence review and an SBOM in CycloneDX and SPDX. On pull requests it reports only what the change introduced. Fixes are ranked by KEV, EPSS and reachability so the exploited issues come first.",
      },
      {
        kind: "p",
        text:
          "It would not have stopped LiteLLM's pipeline from being hijacked, and it would not have stopped a ransomware crew. What it does is make sure that when the next Black October arrives, and it will, your team can answer \"are we affected?\" before lunch rather than by the end of the week.",
      },
    ],
    sources: [
      { label: "StepSecurity: SubQuery ecosystem compromised", url: "https://www.stepsecurity.io/blog/subql-ecosystem-compromised" },
      { label: "The Hacker News: tensorlake npm package compromised", url: "https://thehackernews.com/2026/10/tensorlake-npm-package-compromised-to.html" },
      { label: "BleepingComputer: Rank Math compromised to backdoor websites", url: "https://bleepingcomputer.com/news/security/wordpress-seo-plugin-rank-math-compromised-to-backdoor-websites" },
      { label: "SecurityWeek: hijacked developer account used to plant stealer in OVHcloud CLI", url: "https://www.securityweek.com/hijacked-developer-account-used-to-plant-stealer-in-ovhcloud-cli/" },
      { label: "LiteLLM: security incident, 21 October 2026", url: "https://docs.litellm.ai/blog/security-incident-oct-21-2026" },
      { label: "StepSecurity: GhostAction returns", url: "https://www.stepsecurity.io/blog/ghostaction-returns" },
      { label: "BleepingComputer: OpenCode hack exposes API keys", url: "https://bleepingcomputer.com/news/security/opencode-hack-exposes-api-keys-amid-ai-distillation-attacks" },
      { label: "Railway: security disclosure, 21 October", url: "https://blog.railway.com/p/security-disclosure-wednesday-october-21st" },
      { label: "Rapid7: CVE-2026-109618 and CVE-2026-109619 in PHP", url: "https://www.rapid7.com/blog/post/etr-cve-2026-109618-and-cve-2026-109619-critical-unauthenticated-remote-code-execution-in-php/" },
      { label: "Node.js: October 2026 security releases", url: "https://nodejs.org/en/blog/vulnerability/october-2026-security-releases" },
      { label: "Help Net Security: eMudhra CA private keys compromised", url: "https://www.helpnetsecurity.com/2026/10/21/emudhra-certificates-private-keys-compromised/" },
      { label: "BleepingComputer: ransomware disrupts Japan's IDCF Cloud", url: "https://www.bleepingcomputer.com/news/security/ransomware-attack-disrupts-japans-idcf-cloud-used-by-govt-clients/" },
      { label: "Infosecurity Magazine: Capcom confirms data breach", url: "https://www.infosecurity-magazine.com/news/capcom-confirms-data-breach/" },
      { label: "Verizon 2026 Data Breach Investigations Report", url: "https://www.verizon.com/about/news/breach-industry-wide-dbir-finds" },
    ],
  },
];

export function findArticle(slug: string | undefined): Article | undefined {
  return ARTICLES.find((a) => a.slug === slug);
}
